import { AdminWaiver, GuestType } from "@/constants";
import { Card } from "../ui/card";
import { useState } from "react";
import { CalendarDaysIcon, CircleDollarSign, Loader2, MailIcon, PhoneCallIcon, Download, Users, Clock, CreditCard, User, UserPlus, Tag } from "lucide-react";
import { formatDate, formatShortCryptoAddress, formatMoneyTotal } from "@/utils/format";
import { apiFetch } from "@/utils";
import { Button } from "../ui/button";
import { useToast } from "@/hooks/use-toast";
import { downloadBlob } from "@/utils/download";

const formatGuestType = (guestType: GuestType | null | undefined): string => {
	if (!guestType) return 'N/A';
	const labels: Record<GuestType, string> = {
		couples: 'Couples',
		single_ladies: 'Single Ladies',
		single_males: 'Single Males',
	};
	return labels[guestType] || guestType;
};

const formatDuration = (months: number | null | undefined): string => {
	if (!months) return 'N/A';
	if (months >= 12) {
		const years = months / 12;
		return years === 1 ? '1 Year' : `${years} Years`;
	}
	return months === 1 ? '1 Month' : `${months} Months`;
};

const AdminWaiverCard: React.FC<{
	groupedMembership: any
}> = ({ groupedMembership }) => {
	const { toast } = useToast();

	const [isDownloadingPdf, setIsDownloadingPdf] = useState<string | null>(null);

	const downloadWaiverPdf = async (waiver: AdminWaiver) => {
		setIsDownloadingPdf(waiver.type);

		try {
			const fullUrl = '/v1' + waiver.pdfUri;

			const response = await apiFetch(fullUrl, {
				method: 'GET',
				withCredentials: true,
			});

			const pdfBlob = await response.blob();
			const pdfFileName = `waiver-${waiver.type}-${waiver.id}.pdf`;

			downloadBlob(pdfBlob, pdfFileName);
		} catch(err: any) {
			toast({
				title: 'PDF Generation Failed',
				description: err.message,
				variant: 'destructive'
			})
		}

		setIsDownloadingPdf(null);
	}

	const membership = groupedMembership.membership;
	const profileImageUrl = groupedMembership.profileImageUrl;

	return <Card className="p-4 flex flex-col gap-4">
		{/* Header row: Name, status badge, membership ID */}
		<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
			<div className="flex items-center gap-3">
				{/* Profile photo */}
				{profileImageUrl ? (
					<img
						src={profileImageUrl}
						alt={groupedMembership.fullName}
						className="h-12 w-12 rounded-full object-cover ring-2 ring-border shrink-0"
					/>
				) : (
					<div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center ring-2 ring-border shrink-0">
						<User size={20} className="text-muted-foreground"/>
					</div>
				)}

				<div className="flex flex-col gap-0">
					<div className="flex items-center gap-2">
						<h3 className="text-lg font-bold">
							{groupedMembership.fullName}
						</h3>
						{membership.status === 'active'
							? <span className="text-xs font-semibold bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">Active</span>
							: <span className="text-xs font-semibold bg-red-500/20 text-red-500 px-2 py-0.5 rounded-full">
								{membership.status === 'expired' ? 'Expired' : 'Inactive'}
							</span>
						}
					</div>
					<span className="text-sm text-muted-foreground">
						Membership #{membership.id}
						{membership.transactionId && <> &middot; Transaction #{membership.transactionId}</>}
					</span>
				</div>
			</div>

			{/* Waiver PDF buttons */}
			<div className="flex gap-2 flex-wrap">
				{groupedMembership.waivers.club_swinkster && (
					<Button
						onClick={() => downloadWaiverPdf(groupedMembership.waivers.club_swinkster)}
						disabled={isDownloadingPdf === 'club_swinkster'}
					>
						{isDownloadingPdf === 'club_swinkster' ? <Loader2 className="mr-2 h-5 w-5 animate-spin"/> : <Download className="mr-2 h-5 w-5"/>}
						Club Swinkster
					</Button>
				)}

				{groupedMembership.waivers.secrets_hideaway && (
					<Button
						onClick={() => downloadWaiverPdf(groupedMembership.waivers.secrets_hideaway)}
						disabled={isDownloadingPdf === 'secrets_hideaway'}
					>
						{isDownloadingPdf === 'secrets_hideaway' ? <Loader2 className="mr-2 h-5 w-5 animate-spin"/> : <Download className="mr-2 h-5 w-5"/>}
						Secrets Hideaway
					</Button>
				)}
			</div>
		</div>

		{/* Membership details grid */}
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm border-t border-border pt-3">
			{/* Plan / Template info */}
			<div className="flex items-center gap-2">
				<Tag size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Plan:</span>
				<span className="font-medium">
					{membership.templateName || membership.type || 'N/A'}
				</span>
			</div>

			{/* Guest Type */}
			<div className="flex items-center gap-2">
				<Users size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Guest Type:</span>
				<span className="font-medium">{formatGuestType(membership.guestType)}</span>
			</div>

			{/* Duration */}
			<div className="flex items-center gap-2">
				<Clock size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Duration:</span>
				<span className="font-medium">{formatDuration(membership.durationMonths)}</span>
			</div>

			{/* Price */}
			<div className="flex items-center gap-2">
				<CreditCard size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Price:</span>
				<span className="font-medium">
					{membership.price ? `$${formatMoneyTotal(membership.price)}` : 'N/A'}
				</span>
			</div>

			{/* Primary Name */}
			<div className="flex items-center gap-2">
				<User size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Primary:</span>
				<span className="font-medium">{membership.primaryName || 'N/A'}</span>
			</div>

			{/* Partner Name */}
			<div className="flex items-center gap-2">
				<UserPlus size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Partner:</span>
				<span className="font-medium">{membership.partnerName || 'N/A'}</span>
			</div>

			{/* Date range */}
			<div className="flex items-center gap-2">
				<CalendarDaysIcon size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Period:</span>
				<span className="font-medium">
					{formatDate(membership.startDate)} - {formatDate(membership.endDate)}
				</span>
			</div>

			{/* Wallet address */}
			<div className="flex items-center gap-2">
				<CircleDollarSign size={14} className="text-muted-foreground shrink-0"/>
				<span className="text-muted-foreground">Wallet:</span>
				<span className="font-medium">{formatShortCryptoAddress(groupedMembership.customerAddress!)}</span>
			</div>
		</div>

		{/* Contact info row */}
		<div className="flex flex-wrap gap-4 text-sm border-t border-border pt-3">
			<a href={`tel:${groupedMembership.phoneNumber}`} className="flex items-center gap-1 hover:underline">
				<PhoneCallIcon size={14} className="text-muted-foreground"/>
				{groupedMembership.phoneNumber}
			</a>
			<a href={`mailto:${groupedMembership.emailAddress}`} className="flex items-center gap-1 hover:underline">
				<MailIcon size={14} className="text-muted-foreground"/>
				{groupedMembership.emailAddress}
			</a>
		</div>
	</Card>
}

export default AdminWaiverCard;
